/**
 * Moments in time (a message sent, a record updated), shown on the CLUB's
 * clock.
 *
 * Calendar dates (a birthday, a due date) are not instants: use the
 * date-only helpers of @mediatechgroup/calendar-date for those.
 *
 * Two things made the app show the wrong day and hour (2026-10-09, about
 * 9:45 PM in New York):
 *
 * 1. Many tables store their timestamps WITHOUT a zone (`timestamp`, not
 *    `timestamptz`) - memberships, member_messages, waivers - and the value is
 *    UTC. The API returns it as `2026-10-10T01:36:51` with no `Z`, and
 *    `new Date()` reads a zone-less string as LOCAL time, so a lead updated at
 *    9:36 PM showed "Oct 10, 1:36 AM" while the same lead's zoned
 *    "Stage Changed" read correctly: two formatters on one panel, 5 hours
 *    apart for the same moment.
 * 2. Formatting used the browser's zone, not the club's (settings.timezone).
 *
 * parseStoredInstant reads a zone-less timestamp as UTC; formatClubInstant
 * shows it in the club's zone.
 *
 * Since 2026-10-18 the parsing and formatting come from
 * @mediatechgroup/calendar-date (PM 36ece9c6), whose formatInstantInZone is
 * this file's formatClubInstant with the zone as a parameter. The package
 * never reads browser storage, so the saved-settings reader (clubTimeZone)
 * stays here and is the default zone. src/utils/__tests__/
 * clubTime.pinned.test.ts pins every answer the pre-package copy gave.
 */
import {
  parseStoredInstant,
  formatInstantInZone,
  ZONED_DATE,
  ZONED_LONG_DATE,
  ZONED_DATE_TIME,
} from "@mediatechgroup/calendar-date";
import { safeGetItem } from "@mediatechgroup/app-version";

export { parseStoredInstant };

/** The club's IANA time zone from the saved club settings; undefined when unknown. */
export function clubTimeZone(): string | undefined {
  try {
    // Safe read: public pages (the event sign-up a QR code opens) reach this,
    // and bare localStorage throws in Safari private mode.
    const raw = safeGetItem("settings");
    const tz = raw ? JSON.parse(raw)?.timezone : undefined;
    return typeof tz === "string" && tz.trim() ? tz.trim() : undefined;
  } catch {
    return undefined;
  }
}

/** Date only, e.g. "Oct 9, 2026". */
export const CLUB_DATE: Intl.DateTimeFormatOptions = ZONED_DATE;
/** Long date, e.g. "October 9, 2026". */
export const CLUB_LONG_DATE: Intl.DateTimeFormatOptions = ZONED_LONG_DATE;
/** Date and time, e.g. "Oct 9, 2026, 9:36 PM". */
export const CLUB_DATE_TIME: Intl.DateTimeFormatOptions = ZONED_DATE_TIME;

/**
 * A stored timestamp formatted on the club's clock (US English). Falls back
 * to the browser's zone when the club's is unknown or invalid. Empty string
 * when the value is unparseable.
 */
export function formatClubInstant(
  value: string | Date | null | undefined,
  options: Intl.DateTimeFormatOptions = CLUB_DATE_TIME,
  timeZone: string | undefined = clubTimeZone()
): string {
  return formatInstantInZone(value, options, timeZone);
}
